import React from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';

import { getWindowObject } from '@eb/feature-detection';
import { setWindowLocation } from '@eb/http';
import { BaseGlobalHeader } from '@eb/eds-global-header';

import {
    getHelpMenu,
    getOrganizeMenu,
    getLogoInfo,
    getSearchInfo,
    getQuickLinks,
    getNarrowMenuLinks,
    getUserMenuInfo,
    getNonUserQuickLinks,
    getCreateEventCTA,
    isHomepage,
} from './utils';
import {
    ENV_SHAPE_PROP_TYPE,
    REQUEST_SHAPE_PROP_TYPE,
    USER_SHAPE_PROP_TYPE,
    FALLBACK_SUGGESTIONS,
} from './constants';
import { getDestinationSuggestions } from './utils/search';
import { getUserLocationFromRequest } from './utils/userLocation';

const SEARCH_SUGGESTIONS_THROTTLE_AMOUNT = 300;
const getDestinstionSuggestionsThrottled = throttle(
    getDestinationSuggestions,
    SEARCH_SUGGESTIONS_THROTTLE_AMOUNT,
);

export default class SiteHeader extends React.Component {
    static propTypes = {
        env: ENV_SHAPE_PROP_TYPE.isRequired,
        request: REQUEST_SHAPE_PROP_TYPE.isRequired,
        user: USER_SHAPE_PROP_TYPE.isRequired,
        onSearchSelect: PropTypes.func,
        hideSearchBar: PropTypes.bool,
        onSearchFocus: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.state = {
            searchQuery: props.env.searchQuery || '',
            searchSuggestions: FALLBACK_SUGGESTIONS,
        };
    }

    componentDidMount() {
        this._setUserLocationOnState();
    }

    _setUserLocationOnState = () => {
        const {
            env: { searchLocation },
        } = this.props;

        this.setState({
            userLocation: getUserLocationFromRequest(searchLocation),
        });
    };

    _handleSearchChange = (searchQuery) => {
        if (!this.props.onSearchFocus) {
            getDestinstionSuggestionsThrottled(
                searchQuery,
                this.state.userLocation,
            ).then((searchSuggestions) => {
                this.setState({ searchQuery, searchSuggestions });
            });
        }
    };

    _handleSearchSubmit = (query) => {
        if (!query) {
            setWindowLocation(`/d/local/all-events`);
        } else {
            setWindowLocation(`/d/?q=${query}`);
        }
    };

    render() {
        const {
            env,
            request,
            user,
            onSearchSelect,
            hideSearchBar,
            onSearchFocus,
            featureFlags,
            onMouseEnterGlobalHeader,
        } = this.props;
        const { searchQuery, searchSuggestions } = this.state;
        const {
            serverUrl,
            adminServerUrl,
            loginUrl,
            logoutUrl,
            signinUrl,
            signupUrl,
            helpUrls,
            organizeUrls,
            ebDomain,
            isManageOverviewActive,
            launchNewNavigationHeader,
            launchNewSignInSignUpLinks,
        } = env;
        const { path: requestPath } = request;
        const { isAuthenticated, canCreateEvents } = user;
        let additionalMenus = [
            getHelpMenu(helpUrls, requestPath, launchNewNavigationHeader),
        ];

        if (isHomepage(requestPath) || launchNewNavigationHeader) {
            additionalMenus = [
                getOrganizeMenu(
                    organizeUrls,
                    requestPath,
                    launchNewNavigationHeader,
                ),
                ...additionalMenus,
            ];
        }

        const signinPath = launchNewSignInSignUpLinks ? signinUrl : loginUrl;
        const nonUserQuickLinks = getNonUserQuickLinks(
            requestPath,
            isAuthenticated,
            signinPath,
            signupUrl,
            { launchNewNavigationHeader, launchNewSignInSignUpLinks },
        );
        // We only want to show the Create Event CTA in the header on desktop when the launchNewNavigationHeader
        // is OFF and the user can create an event or it's the homepage
        const showCreateEventCTA = canCreateEvents || isHomepage(requestPath);
        const callToActionInfo = getCreateEventCTA(
            serverUrl,
            ebDomain,
            requestPath,
            isAuthenticated,
            { showCreateEventCTA, launchNewNavigationHeader },
        );
        // We want to be able to customize the mobile header for the new nav experiment. This is a work around
        // that we would like to improve later once the results of the experiment are known. This ticket
        // EB-149406 can be used to find updates
        const narrowMenuLinks = launchNewNavigationHeader
            ? getNarrowMenuLinks({
                  serverUrl,
                  requestPath,
                  nonUserQuickLinks,
                  launchNewSignInSignUpLinks,
              })
            : null;

        return (
            <BaseGlobalHeader
                logoInfo={getLogoInfo(serverUrl, requestPath)}
                // Search Bar
                searchInfo={getSearchInfo(
                    serverUrl,
                    searchQuery,
                    searchSuggestions,
                    hideSearchBar,
                )}
                // Quick links to show in desktop and mobile header
                quickLinks={getQuickLinks(
                    serverUrl,
                    ebDomain,
                    requestPath,
                    launchNewNavigationHeader,
                )}
                // Links for logged in users
                userMenuInfo={getUserMenuInfo({
                    serverUrl,
                    adminServerUrl,
                    logoutUrl,
                    requestPath,
                    user,
                    isManageOverviewActive,
                    showBrowseEvents: hideSearchBar,
                    featureFlags,
                })}
                // Dropdown menus
                additionalMenus={additionalMenus}
                // The signin link for logged out users
                nonUserQuickLinks={nonUserQuickLinks}
                // If the new navbar is not active, show a create event button for logged in users
                callToActionInfo={callToActionInfo}
                // Links we want in the mobile header
                narrowMenuLinks={narrowMenuLinks}
                onSearchChange={this._handleSearchChange}
                onSearchSelect={onSearchSelect}
                onSearchFocus={onSearchFocus}
                onSearchSubmit={this._handleSearchSubmit}
                useNarrowBreakpoint={false}
                launchNewNavigationHeader={launchNewNavigationHeader}
                onUserDropdownMouseEnter={onMouseEnterGlobalHeader}
            />
        );
    }
}
